import React, { useMemo } from 'react';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell
} from 'recharts';

const DoiChart = ({ data, spotPrice }) => {
  // Transform data for horizontal bar chart
  const chartData = useMemo(() => {
    if (!data || !spotPrice) return [];

    const range = spotPrice * 0.05; // 5% range
    return data
      .filter(item => item.strike >= spotPrice - range && item.strike <= spotPrice + range)
      .map(item => ({
        strike: item.strike,
        doi: item.totalDoi / 1000, // Convert to thousands of shares
        callDoi: item.callDoi / 1000,
        putDoi: item.putDoi / 1000,
        isPositive: item.totalDoi >= 0
      }));
  }, [data, spotPrice]);

  // Calculate the max absolute value for symmetric axis
  const maxAbsDoi = useMemo(() => {
    if (!chartData.length) return 1;
    const max = Math.max(...chartData.map(d => Math.abs(d.doi)));
    return max > 0 ? max * 1.1 : 1;
  }, [chartData]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    return (
      <div style={styles.tooltip}>
        <div style={styles.tooltipTitle}>Strike: {data.strike}</div>
        <div style={{ color: data.doi >= 0 ? '#00ccff' : '#ff9900' }}>
          Net DOI: {data.doi >= 0 ? '+' : ''}{data.doi.toFixed(0)}K shares
        </div>
        <div style={{ color: '#00ccff' }}>
          Call DOI: +{data.callDoi.toFixed(0)}K shares
        </div>
        <div style={{ color: '#ff9900' }}>
          Put DOI: {data.putDoi.toFixed(0)}K shares
        </div>
      </div>
    );
  };

  if (!chartData.length) {
    return (
      <div style={styles.noData}>
        No DOI data available
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.chartTitle}>Delta-weighted Open Interest (DOI)</h2>
      <div style={styles.legend}>
        <span style={{ ...styles.legendItem, color: '#00ccff' }}>Bullish (Call DOI)</span>
        <span style={{ ...styles.legendItem, color: '#ff9900' }}>Bearish (Put DOI)</span>
      </div>
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          layout="vertical"
          data={chartData}
          margin={{ top: 20, right: 30, left: 60, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#30363d" horizontal={true} vertical={true} />
          <XAxis
            type="number"
            domain={[-maxAbsDoi, maxAbsDoi]}
            tick={{ fill: '#8b949e', fontSize: 12 }}
            tickFormatter={(value) => `${value >= 0 ? '+' : ''}${(value / 1000).toFixed(0)}M`}
            axisLine={{ stroke: '#30363d' }}
          />
          <YAxis
            type="category"
            dataKey="strike"
            tick={{ fill: '#8b949e', fontSize: 11 }}
            width={55}
            axisLine={{ stroke: '#30363d' }}
            tickFormatter={(value) => value.toLocaleString()}
            reversed={true}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine x={0} stroke="#58a6ff" strokeWidth={1} />
          {spotPrice && (
            <ReferenceLine
              y={chartData.find(d => Math.abs(d.strike - spotPrice) ===
                Math.min(...chartData.map(c => Math.abs(c.strike - spotPrice))))?.strike}
              stroke="#ffcc00"
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{
                value: `Spot: ${spotPrice.toFixed(0)}`,
                fill: '#ffcc00',
                fontSize: 12,
                position: 'right'
              }}
            />
          )}
          <Bar dataKey="doi" name="DOI" radius={[0, 4, 4, 0]}>
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isPositive ? '#00ccff' : '#ff9900'}
                fillOpacity={0.8}
              />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
      <div style={styles.footer}>
        <p style={styles.footerText}>
          DOI = Delta × Open Interest. Positive = bullish directional exposure, Negative = bearish.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#161b22',
    borderRadius: '12px',
    border: '1px solid #30363d',
    padding: '24px'
  },
  chartTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: '#ffffff'
  },
  legend: {
    display: 'flex',
    gap: '24px',
    marginBottom: '16px',
    fontSize: '12px'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  tooltip: {
    backgroundColor: '#1c2128',
    border: '1px solid #30363d',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '13px',
    lineHeight: 1.6
  },
  tooltipTitle: {
    fontWeight: 600,
    color: '#ffffff',
    marginBottom: '4px'
  },
  footer: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #30363d'
  },
  footerText: {
    margin: 0,
    fontSize: '12px',
    color: '#8b949e',
    lineHeight: 1.5
  },
  noData: {
    textAlign: 'center',
    padding: '48px',
    color: '#8b949e',
    backgroundColor: '#161b22',
    borderRadius: '12px',
    border: '1px solid #30363d'
  }
};

export default DoiChart;
